import { useEffect, useState } from 'react';
import { Button, Checkbox, Modal } from '@indipia/ui-core';
import { worklogApi } from '../api/worklogApi';
import type { IndiPhotoItem } from '../types/worklog.types';
import { PhotoLightbox } from './PhotoLightbox';
import { ThumbImg } from './ThumbImg';

/**
 * 인디포토 사진첩 선택 팝업 (작업요청 작성 §2.3 — `source=INDI_PHOTO`).
 *
 * - 선택 현장의 인디포토 사진을 썸네일 그리드(인디포토 미분류와 동일 85×85, 한 줄 5열)로 보여준다.
 * - 썸네일 클릭 = 선택 토글(우상단 체크 + 상단 선택 개수 실시간 갱신), 돋보기 = 크게보기.
 * - 인디포토 미사용자이거나 해당 현장 사진이 없으면 안내 문구만 노출한다.
 *
 * 선택 결과는 photoId 로 반환된다 — 첨부 시 파일 업로드 없이 `photoId` 로 등록된다(fileId xor photoId).
 */

/** 그리드 썸네일 한 변(px) — 인디포토 미분류 카드와 동일 규격 */
const THUMB = 85;
/** 한 줄 열 수 — 6×85 + 간격 5×8 = 550px. modal-lg 본문 폭(640−좌우 18)에 들어간다 */
const COLS = 6;

const EMPTY_GUIDE = '인디포토에서 사진을 정리해 두면 자유롭게 활용하실 수 있어요.';

export interface PickedIndiPhoto {
  photoId: number;
  /** 원본 파일ID — 응답에 있으면 첨부 시 photoId 와 함께 넘긴다 */
  fileId?: number | string | null;
  thumbUrl: string | null;
}

export function IndiPhotoPickerModal({
  open,
  siteId,
  siteName,
  subscribed,
  room,
  onClose,
  onConfirm,
}: {
  open: boolean;
  /** 선택된 현장 siteId — 없으면 조회하지 않는다 */
  siteId: number | null;
  /** 선택된 현장명 — 어느 현장의 사진첩인지 헤더에 표시 */
  siteName?: string | null;
  /** 인디포토(agentId 1) 구독 여부 */
  subscribed: boolean;
  /** 더 담을 수 있는 장수(전체 최대 장수 − 현재 첨부 장수) */
  room: number;
  onClose: () => void;
  onConfirm: (picked: PickedIndiPhoto[]) => void;
}) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [photos, setPhotos] = useState<IndiPhotoItem[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [viewIdx, setViewIdx] = useState<number | null>(null);

  // 열릴 때마다 초기화 후 조회 — 구독자가 아니면 호출 자체를 하지 않는다.
  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setViewIdx(null);
    if (!subscribed || siteId == null) {
      setPhotos([]);
      setStatus('ready');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    worklogApi
      .getIndiPhotos(siteId, 100, 0, controller.signal)
      .then((res) => {
        setPhotos(res.data.data?.photos ?? []);
        setStatus('ready');
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        // 미구독 계정이 접근하면 403 이 올 수 있다 — 이 경우도 안내 문구로 흡수한다.
        if (e?.response?.status === 403) {
          setPhotos([]);
          setStatus('ready');
          return;
        }
        setStatus('error');
      });
    return () => controller.abort();
  }, [open, siteId, subscribed]);

  const toggle = (photoId: number) => {
    setSelected((prev) => {
      if (prev.includes(photoId)) return prev.filter((id) => id !== photoId);
      if (prev.length >= room) return prev; // 남은 여유를 넘겨 선택할 수 없다
      return [...prev, photoId];
    });
  };

  const confirm = () => {
    const picked = selected
      .map((id) => photos.find((p) => p.photoId === id))
      .filter((p): p is IndiPhotoItem => p != null)
      .map((p) => ({ photoId: p.photoId, fileId: p.fileId ?? null, thumbUrl: p.thumbUrl }));
    onConfirm(picked);
  };

  const full = selected.length >= room;

  return (
    /*
      Modal 의 title(.modal-head)을 쓰지 않고 헤더를 직접 그린다 —
      공통 .modal-head 의 닫기(×)가 제목 기준선보다 아래로 내려와 보이는데,
      공통 CSS는 컨펌 없이 못 고치므로 이 팝업에서만 정렬을 잡는다(D-09 §15.6 구성은 동일).
    */
    <Modal open={open} onClose={onClose} size="lg">
      <div className="flex w-full flex-col">
        <div className="flex items-center justify-between pb-2 pt-3">
          <h4 className="text-title-md font-bold text-text">인디포토 사진첩</h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-6 w-6 items-center justify-center rounded-md text-text-secondary hover:bg-surface-muted"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* 상단 요약 — 사진 수·현장명 + 선택 개수 실시간 표시 */}
        <div className="flex items-center justify-between border-b border-border pb-2">
          <span className="flex items-center gap-2 text-body-sm text-text-secondary">
            {status === 'ready' && photos.length > 0 && <span>사진 {photos.length}장</span>}
            {siteName && (
              <span className="truncate font-medium text-text" title={siteName}>
                {siteName}
              </span>
            )}
          </span>
          <span className="text-body-sm text-text-secondary">
            선택 <b className="font-bold text-group">{selected.length}</b> 장 
          </span>
        </div>

        {status === 'loading' && (
          <div className="py-16 text-center text-body-sm text-text-muted">불러오는 중…</div>
        )}

        {status === 'error' && (
          <div className="py-16 text-center text-body-sm text-text-muted">
            인디포토 사진을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
          </div>
        )}

        {/* 미구독자 · 해당 현장 사진 없음 — 동일 안내 */}
        {status === 'ready' && photos.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-text-disabled">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="8.5" cy="10" r="1.5" />
              <path d="M21 16l-5-5-5 5-2-2-3.5 3.5" />
            </svg>
            <p className="text-body-sm text-text-secondary">{EMPTY_GUIDE}</p>
          </div>
        )}

        {status === 'ready' && photos.length > 0 && (
          <div
            className="grid max-h-[420px] justify-center gap-2 overflow-y-auto py-3"
            style={{ gridTemplateColumns: `repeat(${COLS}, ${THUMB}px)` }}
          >
            {photos.map((p, i) => {
              const on = selected.includes(p.photoId);
              return (
                <div key={p.photoId} className="relative" style={{ width: THUMB, height: THUMB }}>
                  <button
                    type="button"
                    onClick={() => toggle(p.photoId)}
                    aria-pressed={on}
                    aria-label={`사진 ${i + 1} ${on ? '선택 해제' : '선택'}`}
                    disabled={!on && full}
                    className={`h-full w-full overflow-hidden rounded-lg border-base transition-colors disabled:cursor-not-allowed ${on ? '' : 'border-border hover:border-group'}`}
                    style={{
                      borderColor: on ? 'var(--gt-primary)' : undefined,
                      boxShadow: on ? '0 0 0 3px var(--gt-subtle)' : 'none',
                      opacity: !on && full ? 0.45 : 1,
                    }}
                  >
                    <ThumbImg url={p.thumbUrl} className="h-full w-full" alt="" />
                  </button>

                  {/* 선택 체크박스 — 좌상단. 브라우저 기본 checkbox margin이 배경 패딩과 겹쳐 비대칭으로 보이는 것을 m-0으로 제거 */}
                  <span className="absolute left-01 top-01 flex rounded-sm bg-surface p-01">
                    <Checkbox
                      bare
                      className="m-0"
                      checked={on}
                      disabled={!on && full}
                      onChange={() => toggle(p.photoId)}
                      aria-label={`사진 ${i + 1} ${on ? '선택 해제' : '선택'}`}
                    />
                  </span>

                  {/* 크게보기 — 썸네일 클릭은 선택이므로 확대는 별도 버튼으로 분리 */}
                  <button
                    type="button"
                    onClick={() => setViewIdx(i)}
                    aria-label={`사진 ${i + 1} 크게보기`}
                    title="크게보기"
                    className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-full"
                    style={{ background: 'var(--alpha-black-600)', color: 'var(--gray-0)' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-2 flex items-center justify-end gap-2 border-t border-border pt-3">
          {full && (
            <span className="mr-auto flex items-center gap-1 text-caption text-text-muted">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              더 담으려면 첨부된 사진을 먼저 지워주세요.
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" disabled={selected.length === 0} onClick={confirm}>
            첨부
          </Button>
        </div>
      </div>

      {/* 크게보기 — 상단 우측에 선택 체크 토글 */}
      <PhotoLightbox
        photos={photos.map((p) => ({ url: p.thumbUrl ?? '' }))}
        index={viewIdx}
        onClose={() => setViewIdx(null)}
        onIndexChange={setViewIdx}
        renderImage={(photo, style) => <ThumbImg url={photo.url} imgStyle={style} />}
        headerRight={
          viewIdx != null ? (
            <ViewerCheck
              on={selected.includes(photos[viewIdx].photoId)}
              disabled={!selected.includes(photos[viewIdx].photoId) && full}
              onToggle={() => toggle(photos[viewIdx].photoId)}
            />
          ) : null
        }
      />
    </Modal>
  );
}

/** 크게보기 상단 우측 선택 토글 — 어두운 오버레이 위라 ui-core Button 대신 전용 스타일(PhotoLightbox IconBtn과 동일 사유) */
function ViewerCheck({ on, disabled, onToggle }: { on: boolean; disabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={on}
      className="flex h-12 items-center gap-1.5 rounded-full px-3 text-body-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40"
      style={{
        background: on ? 'var(--gt-primary)' : 'var(--alpha-white-200)',
        color: 'var(--gray-0)',
      }}
    >
      <span
        className="flex h-[18px] w-[18px] items-center justify-center rounded-full border-2"
        style={{ borderColor: 'var(--gray-0)', background: on ? 'var(--gray-0)' : 'transparent' }}
      >
        {on && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      {on ? '선택됨' : '선택'}
    </button>
  );
}
